import { when } from 'mobx';
import { UserStore } from './UserStore';
import { GamesStore } from './GamesStore';
import { HighlightsStore } from './HighlightsStore';
import { PostsStore } from './PostsStore';
import { OrganizationStore } from './OrganizationStore';

export class RootStore {
  constructor() {
    // Construct all the stores
    this.userStore = new UserStore(this);
    this.gamesStore = new GamesStore(this);
    this.highlightsStore = new HighlightsStore(this);
    this.postsStore = new PostsStore(this);
    this.organizationStore = new OrganizationStore(this);

    // Set API URL (same as mobile app)
    this.apiUrl = process.env.REACT_APP_API_URL || 'https://www.flyoverfabric.com/api';

    // Initialize user store
    this.userStore.initialize();

    // Wait until userStore is done so we have a token to use for API calls
    when(
      () => !this.userStore.initializing,
      () => {
        // Initialize other stores after user is authenticated
        if (this.userStore.accessToken) {
          this.gamesStore.fetchGames();
        }
      },
    );
  }

  /**
   * Helper function to use the userStore to authenticate a request
   * @param input: RequestInfo
   * @param init: RequestInit
   * @param method: HTTP method to be used
   * @param data: Optional data to be parsed and passed in body
   * @returns {Promise<Response>}
   */
  async authenticatedFetch(
    input,
    init = {},
    method = 'GET',
    data,
    file = false,
    nonAuthenticatedCall = false
  ) {
    if (this.userStore.accessToken === null && !nonAuthenticatedCall) {
      throw Error('User is not authenticated.');
    }

    console.log('🔑 accessToken', this.userStore.accessToken);

    // Add our server
    if (typeof input === 'string' && !input.startsWith('http')) {
      input = `${this.apiUrl}/${input}`;
      console.log('input', input);
    }

    // Set our method, prefer init.method if provided
    init.method = init.method || method || 'GET';

    // Do we have a body
    if (data && !file) {
      init.body = JSON.stringify(data);
    }

    // Do we have a body
    if (data && file) {
      init.body = data;
    }

    if (!init.hasOwnProperty('headers')) {
      init.headers = {};
    }

    if (!nonAuthenticatedCall) {
      init.headers.Authorization = `Bearer ${this.userStore.accessToken}`;
    }

    if (data && !file) {
      init.headers['Content-Type'] = 'application/json';
    }

    if (data && file) {
      // Don't set Content-Type for FormData - let the browser set it with the correct boundary
      // init.headers['Content-Type'] = 'multipart/form-data';
      // init.headers.enctype = 'multipart/form-data';
    }

    try {
      console.log('🚀 fetching', input, init);
      const response = await fetch(input, init);
      
      // Handle JWT expiration at fetch level
      if (!response.ok && response.status === 401) {
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error === 'jwt expired' || errorData.message?.includes('jwt expired')) {
            console.log('JWT expired in fetch, redirecting to login');
            this.userStore.clearAuthState();
            window.location.href = '/login';
            return response; // Return the response to be handled by authenticatedApiCall
          }
        } catch (parseError) {
          if (errorText.includes('jwt expired') || errorText.includes('JWT expired')) {
            console.log('JWT expired in fetch (from text), redirecting to login');
            this.userStore.clearAuthState();
            window.location.href = '/login';
            return response;
          }
        }
      }
      
      return response;
    } catch (error) {
      console.log('❌ error', error);
      throw error;
    }
  }

  /**
   * Helper function to authenticate and parses the JSON
   * @param input: RequestInfo
   * @param init: RequestInit
   * @param method: HTTP method to be used
   * @param data: Optional data to be parsed and passed in body
   * @returns {Promise<any>}
   */
  async authenticatedApiCall(
    input,
    init = {},
    method = 'GET',
    data,
    file = false,
    stringReturn = false,
    nonAuthenticatedCall = false
  ) {
    console.log('🔍 RootStore.authenticatedApiCall called for:', input);
    console.log('🔍 RootStore.authenticatedApiCall method:', method);
    console.log('🔍 RootStore.authenticatedApiCall data being sent:', data);
    console.log('🔍 RootStore.authenticatedApiCall accessToken available:', !!this.userStore.accessToken);
    console.log('🔍 RootStore.authenticatedApiCall accessToken value:', this.userStore.accessToken);

    try {
      const response = await this.authenticatedFetch(input, init, method, data, file, nonAuthenticatedCall);
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        
        // Handle JWT expiration specifically
        if (response.status === 401) {
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.error === 'jwt expired' || errorData.message?.includes('jwt expired')) {
              console.log('JWT expired, redirecting to login');
              // Clear auth state and redirect to login
              this.userStore.clearAuthState();
              window.location.href = '/login';
              return;
            }
          } catch (parseError) {
            // If we can't parse the error, still check for JWT expiration in the text
            if (errorText.includes('jwt expired') || errorText.includes('JWT expired')) {
              console.log('JWT expired (from text), redirecting to login');
              this.userStore.clearAuthState();
              window.location.href = '/login';
              return;
            }
          }
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get && response.headers.get('content-type');
      if (!file && !stringReturn && contentType && contentType.indexOf('application/json') !== -1) {
        const data = await response.json();
        console.log('Response data:', data);
        return data;
      } else {
        console.warn('Non-JSON response from', input);
        return response.text();
      }
    } catch (error) {
      console.error('Error in authenticatedApiCall:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      throw error; // Re-throw the error so the calling code can handle it
    }
  }
} 